import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, query, where, getDocs, collection } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase';
import { useAuth } from '../AuthContext';
import './createReview.css';

export default function CreateReview() {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const [booking, setBooking] = useState(null);
  const [meal, setMeal] = useState(null);
  const [reviewerRole, setReviewerRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [existingReview, setExistingReview] = useState(null);

  // Form state
  const [culinaryRating, setCulinaryRating] = useState(0);
  const [serviceRating, setServiceRating] = useState(0);
  const [cleanlinessRating, setCleanlinessRating] = useState(0);
  const [reviewText, setReviewText] = useState('');

  useEffect(() => {
    const fetchBookingAndMeal = async () => {
      if (!currentUser || !bookingId) {
        setError('Missing required information');
        setLoading(false);
        return;
      }

      try {
        const bookingRef = doc(db, 'bookings', bookingId);
        const bookingSnap = await getDoc(bookingRef);

        if (!bookingSnap.exists()) {
          setError('Booking not found');
          setLoading(false);
          return;
        }

        const bookingData = { id: bookingSnap.id, ...bookingSnap.data() };
        setBooking(bookingData);

        const isHost = currentUser.uid === bookingData.hostId;
        const isGuest = currentUser.uid === bookingData.guestId;

        if (!isHost && !isGuest) {
          setError('You are not authorized to review this booking');
          setLoading(false);
          return;
        }

        const role = isHost ? 'host' : 'guest';
        setReviewerRole(role);

        const mealRef = doc(db, 'meals', bookingData.mealId);
        const mealSnap = await getDoc(mealRef);

        if (mealSnap.exists()) {
          setMeal({ id: mealSnap.id, ...mealSnap.data() });
        }

        const existingReviewQuery = query(
          collection(db, 'reviews'),
          where('bookingId', '==', bookingId),
          where('reviewerRole', '==', role)
        );
        const existingReviewSnap = await getDocs(existingReviewQuery);

        if (!existingReviewSnap.empty) {
          setExistingReview(existingReviewSnap.docs[0].data());
        }

        setLoading(false);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load booking information');
        setLoading(false);
      }
    };

    fetchBookingAndMeal();
  }, [currentUser, bookingId]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (culinaryRating === 0 || serviceRating === 0 || cleanlinessRating === 0) {
      setError('Please provide all ratings');
      return;
    }

    if (reviewText.length > 1000) {
      setError('Review text must be 1000 characters or less');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const submitReview = httpsCallable(functions, 'submitReview');
      const result = await submitReview({
        bookingId,
        culinaryRating,
        serviceRating,
        cleanlinessRating,
        reviewText: reviewText.trim() || null,
      });

      if (result.data.isVisible) {
        alert('Review submitted! Both reviews are now visible.');
      }

      navigate(-1);
    } catch (err) {
      console.error('Error submitting review:', err);
      const errorMessage = err.code === 'already-exists'
        ? 'You have already submitted a review for this booking.'
        : err.code === 'permission-denied'
        ? 'You are not authorized to review this booking.'
        : err.message || 'Failed to submit review. Please try again.';
      setError(errorMessage);
      setSubmitting(false);
    }
  };

  const StarRating = ({ value, onChange, label }) => (
    <div className="star-rating-group">
      <label className="star-rating-label">{label}</label>
      <div className="star-rating-buttons">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => onChange(star)}
            className={`star-button ${star <= value ? 'active' : ''}`}
          >
            ★
          </button>
        ))}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner" />
        Loading...
      </div>
    );
  }

  if (existingReview) {
    return (
      <div className="review-container">
        <div className="state-card warning">
          <h2>Review Already Submitted</h2>
          <p>You have already submitted a review for this booking.</p>
          <button onClick={() => navigate(-1)} className="btn btn-primary">
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (error && !booking) {
    return (
      <div className="review-container">
        <div className="state-card error">
          <h2>Error</h2>
          <p>{error}</p>
          <button onClick={() => navigate(-1)} className="btn btn-primary">
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="review-container">
      <div className="review-header">
        <h1>Write a Review</h1>
        <p>
          {reviewerRole === 'guest'
            ? 'Share your experience about the meal and host'
            : 'Share your experience hosting this guest'}
        </p>
      </div>

      {meal && (
        <div className="meal-info-card">
          <h3>{meal.title}</h3>
          {meal.dateTime && (
            <p>
              {meal.dateTime.toDate
                ? meal.dateTime.toDate().toLocaleDateString('en-US', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })
                : new Date(meal.dateTime).toLocaleDateString()}
            </p>
          )}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <StarRating
          value={culinaryRating}
          onChange={setCulinaryRating}
          label={reviewerRole === 'guest' ? 'Food Quality' : 'Guest Conduct'}
        />

        <StarRating
          value={serviceRating}
          onChange={setServiceRating}
          label="Communication & Hospitality"
        />

        <StarRating
          value={cleanlinessRating}
          onChange={setCleanlinessRating}
          label={reviewerRole === 'guest' ? 'Cleanliness & Environment' : 'Respectfulness'}
        />

        <div className="review-text-group">
          <label>Your Review (Optional)</label>
          <textarea
            value={reviewText}
            onChange={(e) => setReviewText(e.target.value)}
            placeholder={
              reviewerRole === 'guest'
                ? 'Tell others about your experience...'
                : 'Share how the guest was during the meal...'
            }
            maxLength={1000}
            className="review-textarea"
          />
          <p className={`char-count ${reviewText.length > 900 ? 'warning' : ''}`}>
            {reviewText.length}/1000
          </p>
        </div>

        {error && (
          <div className="notice-box error">
            {error}
          </div>
        )}

        <div className="notice-box info">
          Your review will become visible once both parties have submitted their reviews.
        </div>

        <div className="button-group">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="btn btn-secondary"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="btn btn-primary"
          >
            {submitting ? 'Submitting...' : 'Submit Review'}
          </button>
        </div>
      </form>
    </div>
  );
}
